// src/pages/ManualInput.js
import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import CustomButton from '../components/CustomButton';
import CustomModal from '../components/CustomModal';
import PatientInfoForm from '../components/PatientInfoForm';
import api from "../api";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// 共通スキーマ（DB定義書のキー名・日本語ラベル）
import {
  FIELDS,
  SECTION_GROUPS,
  buildOptionsFromFields,
  labelOf,
  typeOf,
  isRequired,
} from '../schema/patientSchema';

dayjs.extend(utc);
dayjs.extend(timezone);

// 初期値はスキーマから自動生成（全キーを '' に）
const EMPTY_VALUES = FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: '' }), {});

// 過去データ1回目は必須
const REQUIRED_FIRST_TREATMENT_FIELDS = [
  {
    key: 'treatment_start_1',
    label: '過去データ1回目：診察日',
    type: 'date',
  },
  {
    key: 'treatment_drug_1',
    label: '過去データ1回目：薬剤名',
    type: 'enum',
  },
];

const EXTRA_REQUIRED_KEYS = REQUIRED_FIRST_TREATMENT_FIELDS.map((f) => f.key);

const isFirstTreatmentRequired = (key) =>
  REQUIRED_FIRST_TREATMENT_FIELDS.some((f) => f.key === key);

const firstTreatmentLabelOf = (key) =>
  REQUIRED_FIRST_TREATMENT_FIELDS.find((f) => f.key === key)?.label;

// 厳密日付（ISO等）→ YYYY年M月D日
function formatDateLabel(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
}

// partialDate（YYYY / YYYY/MM / YYYY/MM/DD or ISO）→ 和式
function formatPartialDateLabel(v) {
  if (!v) return '—';
  const s = String(v).trim();
  if (/^\d{4}$/.test(s)) return `${s}年`;
  const m = s.replace(/-/g, '/').match(/^(\d{4})\/(\d{1,2})(?:\/(\d{1,2}))?$/);
  if (m) {
    const y = Number(m[1]);
    const mm = Number(m[2]);
    const dd = m[3] ? Number(m[3]) : null;
    return dd ? `${y}年${mm}月${dd}日` : `${y}年${mm}月`;
  }
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
  }
  return s || '—';
}

function normalizeDate(v) {
  if (!v) return "";

  // 区切りを全部 "-" に統一
  const s = v.trim().replace(/\//g, "-");

  // 1. 年のみ 例: 2012
  if (/^\d{4}$/.test(s)) {
    return `${s}-01-01`;
  }

  // 2. 年-月 例: 2012-06
  if (/^\d{4}-\d{1,2}$/.test(s)) {
    const [y, m] = s.split("-");
    return `${y}-${m.padStart(2, "0")}-01`;
  }

  // 3. 年-月-日 例: 2012-06-06
  if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(s)) {
    const [y, m, d] = s.split("-");
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }

  // どれにも当てはまらない場合は空を返す（不正値）
  return "";
}

// 空判定（0 は空扱いにしない）
const isEmpty = (v) => v === '' || v === null || v === undefined;

export default function ManualInput() {
  const navigate = useNavigate();
  const options = useMemo(() => buildOptionsFromFields(), []);
  const [values, setValues] = useState(EMPTY_VALUES);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [errors, setErrors] = useState([]);

  const onChange = (id, value) => setValues((p) => ({ ...p, [id]: value }));

  const displayLabelOf = (k) => firstTreatmentLabelOf(k) || labelOf(k);

  const isPreviewRequired = (k) => isRequired(k) || isFirstTreatmentRequired(k);

  // スキーマ必須 + 過去データ1回目必須
  const validate = () => {
    const errs = [];

    // 既存スキーマ準拠の必須項目
    for (const f of FIELDS) {
      if (!f.required) continue;

      const v = values[f.key];

      if (isEmpty(v)) {
        const t = typeOf(f.key);

        if (t === 'enum') {
          errs.push(`${labelOf(f.key)}を選択してください。`);
        } else {
          errs.push(`${labelOf(f.key)}は必須です。`);
        }
      }
    }

    // 過去データ1回目：consultation_date / drug_type 相当を必須化
    for (const f of REQUIRED_FIRST_TREATMENT_FIELDS) {
      const v = values[f.key];

      if (isEmpty(v)) {
        if (f.type === 'enum') {
          errs.push(`${f.label}を選択してください。`);
        } else {
          errs.push(`${f.label}は必須です。`);
        }
      }
    }

    setErrors(errs);
    return errs.length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    setConfirmOpen(true);
  };

  const doRegister = async () => {
    const raw = sessionStorage.getItem("auth");
    const auth = raw ? JSON.parse(raw) : null;

    if (!validate()) return;

    try {
      const payload = {
        doctor_id: auth?.id || "",
        consultation_id: values.consultation_id || "",
        research_id: values.research_id || "",
        pw: values.patient_pw || "",

        date_of_birth: toYMD(values.date_of_birth) || "1900-01-01",
        gender_type: values.gender_type || "不明",
        ms_type: values.ms_type || "不明",

        hope_child: values.hope_child || "",
        smoking: values.smoking || "",
        drinking: values.drinking || "",

        complications: values.complications || "",
        medical_history: values.medical_history || "",
        family_history: values.family_history || "",

        first_detect_date: (values.first_detect_date || "").replace(/\//g, "-"),
        first_detect_way: values.first_detect_way || "",
        first_onset_date: (values.first_onset_date || "").replace(/\//g, "-"),
        first_onset_location: values.first_onset_location || "",
        first_onset_symptom: values.first_onset_symptom || "",
        last_onset_date: (values.last_onset_date || "").replace(/\//g, "-"),

        recurrences_times: values.recurrences_times || "",

        pira_presence: values.pira_presence || "",
        pira_suspected_date: (values.pira_suspected_date || "").replace(/\//g, "-"),
        pira_symptoms: values.pira_symptoms || "",

        last_edss3_date: (values.last_edss3_date || "").replace(/\//g, "-"),
        last_edss6_date: (values.last_edss6_date || "").replace(/\//g, "-"),

        aqp4_test: values.aqp4_test || "",
        mog_test: values.mog_test || "",
        antibody_test: values.antibody_test || "",
        cerebrospinal_fluid_test: values.cerebrospinal_fluid_test || "",
        fluid_test_date: (values.fluid_test_date || "").replace(/\//g, "-"),
        ocb_result_type: values.ocb_result_type || "",
        fluid_test_company: values.fluid_test_company || "",
        igg_result_score: Number(values.igg_result_score || 0),

        date_of_answer: dayjs().tz("Asia/Tokyo").format("YYYY-MM-DD"),
      };

      console.log(payload);

      const res = await api.post("/v1/doctor/new_registration", payload);
      console.log("✅ new_registration 成功:", res.data);

      // === ② 診察記録登録 ===
      const consultationRecords = [];

      // 0回目：初回ベース登録。これは必ず登録する。
      consultationRecords.push({
        research_id: payload.research_id,
        consultation_date: dayjs().tz("Asia/Tokyo").format("YYYY-MM-DD"),
        edss_score: Number(values.edss_score || 0),
        drug_type: "",
        mri_result: "",
        recent_recurrence: "",
        doctor_comment: "",
        update_date: dayjs().tz("Asia/Tokyo").format("YYYY-MM-DDTHH:mm:ss.SSS"),
      });

      // 1〜10回目：過去データ登録。
      // 1回目は validate() で必須保証済み。
      // 2回目以降は、drug/start が両方空ならスキップ。
      for (let i = 1; i <= 10; i++) {
        const drug = values[`treatment_drug_${i}`];
        const start = values[`treatment_start_${i}`];

        if (i !== 1 && !drug && !start) continue;

        consultationRecords.push({
          research_id: payload.research_id,
          consultation_date: normalizeDate(start),
          edss_score: null,
          drug_type: drug,
          mri_result: "",
          recent_recurrence: "",
          doctor_comment: "",
          update_date: dayjs().tz("Asia/Tokyo").format("YYYY-MM-DDTHH:mm:ss.SSS"),
        });
      }

      console.log(consultationRecords);

      await Promise.all(
        consultationRecords.map((r) =>
          api.post("/v1/doctor/register_medical_info", r)
        )
      );

      console.log(`🩺 register_medical_info 成功 (${consultationRecords.length}件)`);

      alert("登録が完了しました！");
      setConfirmOpen(false);
      navigate("/patient-selection", { replace: true });

    } catch (err) {
      console.error("登録エラー:", err);
      alert("登録に失敗しました。");
    }
  };

  // プレビュー順：セクション順 + 過去データ1回目
  const previewOrder = [
    ...SECTION_GROUPS.personal,
    ...SECTION_GROUPS.history,
    ...SECTION_GROUPS.lab,
    'treatment_start_1',
    'treatment_drug_1',
  ];

  const renderPreviewValue = (k) => {
    const v = values[k];

    if (!v && v !== 0) return '—';

    if (k === 'treatment_start_1') return formatPartialDateLabel(v);
    if (k === 'treatment_drug_1') return String(v);

    const t = typeOf(k);

    if (t === 'date') return formatDateLabel(v);
    if (t === 'partialDate') return formatPartialDateLabel(v);
    if (k === 'edss_score' && v === '') return '—';
    if (k === 'date_of_birth' && v) return formatDateLabel(v);

    return String(v);
  };

  // 必須バッジ（プレビュー用）
  const RequiredBadge = () => (
    <span style={{ color: '#da1e28', fontWeight: 700, marginLeft: 6 }}>*必須</span>
  );

  return (
    <>
      <div style={{ minHeight: '100vh', background: '#f4f4f4' }}>
        <div style={{ maxWidth: 960, margin: '0 auto', padding: '1rem' }}>
          <div style={{ marginBottom: '0.5rem' }}>
            <Link
              to="/patient-registration"
              className="sidebar-link"
              style={{ textDecoration: 'none', color: '#0062ff', fontWeight: 'bold' }}
            >
              ← 登録方法選択に戻る
            </Link>
          </div>

          <h2 style={{ margin: '1rem 0' }}>新規患者の登録（手動）</h2>

          {errors.length > 0 && (
            <div
              style={{
                marginBottom: '1rem',
                background: '#fff',
                border: '1px solid #fa4d56',
                borderRadius: 6,
                color: '#750e13',
                padding: '0.75rem 1rem',
              }}
              aria-live="assertive"
            >
              <div style={{ fontWeight: 600, marginBottom: 6 }}>
                入力内容を確認してください
              </div>
              {errors.map((e, i) => (
                <div key={i}>・{e}</div>
              ))}
            </div>
          )}

          <div
            style={{
              background: '#fff',
              borderRadius: 6,
              padding: '1rem 1.25rem',
              boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
            }}
          >
            <PatientInfoForm
            values={values}
            onChange={onChange}
            options={options}
            includeEdss={true}
            includeDrugHistory={true}
            extraRequiredKeys={EXTRA_REQUIRED_KEYS}
            />
          </div>

          <div
            style={{
              display: 'flex',
              gap: 12,
              marginTop: 16,
              justifyContent: 'flex-end',
            }}
          >
            <CustomButton kind="ghost" onClick={() => setValues(EMPTY_VALUES)}>
              クリア
            </CustomButton>
            <CustomButton kind="primary" onClick={handleSubmit}>
              登録
            </CustomButton>
          </div>
        </div>
      </div>

      <CustomModal
        open={confirmOpen}
        title="この内容で登録しますか？"
        onClose={() => setConfirmOpen(false)}
        size="lg"
        closeOnOverlay={true}
        footer={
          <>
            <CustomButton kind="secondary" onClick={() => setConfirmOpen(false)}>
              戻る
            </CustomButton>
            <CustomButton kind="primary" onClick={doRegister}>
              登録する
            </CustomButton>
          </>
        }
      >
        <div style={{ maxHeight: '60vh', overflow: 'auto', fontSize: '12px', lineHeight: 1.4 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {previewOrder.map((k) => (
                <tr key={k}>
                  <th
                    style={{
                      width: 280,
                      textAlign: 'left',
                      padding: '6px 8px',
                      borderBottom: '1px solid #f2f2f2',
                      background: '#fafafa',
                    }}
                  >
                    {displayLabelOf(k)}
                    {isPreviewRequired(k) && <RequiredBadge />}
                  </th>
                  <td
                    style={{
                      padding: '6px 8px',
                      borderBottom: '1px solid #f2f2f2',
                    }}
                  >
                    {renderPreviewValue(k)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CustomModal>
    </>
  );
}

function toYMD(v) {
  if (!v) return "";

  try {
    const d = new Date(v);

    if (Number.isNaN(d.getTime())) return "";

    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");

    return `${y}-${m}-${day}`;
  } catch {
    return "";
  }
}